import React from "react"
import { useNavigate, useRouteLoaderData } from "react-router-dom"
import DatasetCard from "./DatasetCard"
import { Datasets, DatasetSchemas, CurrentOrg } from "../lib/store"
import { Api } from "../lib/api"
import { getPlanLimits } from "../lib/limits"
import {
  Alert,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Link,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  OrderedList,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  TableContainer,
  VStack,
} from "@chakra-ui/react"
import { AddIcon, DeleteIcon, DragHandleIcon } from "@chakra-ui/icons"
import { Form } from "react-router-dom"
import AppLink from "./Link"
import DeleteConfirmModal from "./DeleteConfirmModal"
import { isOrgAdmin } from "../lib/util"

function GridIcon(props) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="currentColor"
      width="1em"
      height="1em"
      {...props}
    >
      <path d="M1 2.5A1.5 1.5 0 012.5 1h3A1.5 1.5 0 017 2.5v3A1.5 1.5 0 015.5 7h-3A1.5 1.5 0 011 5.5v-3zm8 0A1.5 1.5 0 0110.5 1h3A1.5 1.5 0 0115 2.5v3A1.5 1.5 0 0113.5 7h-3A1.5 1.5 0 019 5.5v-3zm-8 8A1.5 1.5 0 012.5 9h3A1.5 1.5 0 017 10.5v3A1.5 1.5 0 015.5 15h-3A1.5 1.5 0 011 13.5v-3zm8 0A1.5 1.5 0 0110.5 9h3a1.5 1.5 0 011.5 1.5v3a1.5 1.5 0 01-1.5 1.5h-3A1.5 1.5 0 019 13.5v-3z" />
    </svg>
  )
}

function CreateDatasetModal({ isOpen, onClose }) {
  const [currentOrg] = CurrentOrg.useState()
  const [datasets] = Datasets.useState()
  const [error, setError] = React.useState(null)
  const api = new Api()
  const navigate = useNavigate()

  const handleSubmit = async (event) => {
    event.preventDefault()
    const { name, description } = event.target.elements
    const { maxDatasets } = getPlanLimits(currentOrg)
    if (maxDatasets !== null && Object.keys(datasets).length >= maxDatasets) {
      setError(
        `Your plan supports up to ${maxDatasets} dataset${
          maxDatasets === 1 ? "" : "s"
        }. Upgrade to create more.`,
      )
      return
    }
    try {
      const response = await api.datasetsCreate(
        name.value,
        description.value,
        currentOrg.url,
      )
      onClose()
      setError(null)
      await api.datasetsList(currentOrg.id)
      navigate(`/datasets/${response.id}`)
    } catch (e) {
      setError((await e.json()).detail)
    }
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  return (
    <Modal variant="mini" isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add a new Dataset</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error && <Alert status="error">{error}</Alert>}
          <Form onSubmit={handleSubmit}>
            <VStack gap={4}>
              <VStack gap={1} width="full" alignItems="flex-start">
                <Input
                  variant="primary"
                  name="name"
                  placeholder="My New Dataset"
                  status={error && "error"}
                  id="ds-name"
                  isRequired
                />
                <Text color="black" fontSize="xs">
                  {error || "Give your dataset a name"}
                </Text>
              </VStack>
              <VStack gap={1} width="full" alignItems="left">
                <Input
                  variant="primary"
                  name="description"
                  placeholder="This is a dataset for/of..."
                  id="ds-description"
                />
                <Text color="black" fontSize="xs">
                  And optional description
                </Text>
              </VStack>
              <Flex width="full" gap={2}>
                <Spacer />
                <Button size="sm" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  Create
                </Button>
              </Flex>
            </VStack>
          </Form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function DatasetTable({ datasets, datasetSchemas, canDelete }) {
  const navigate = useNavigate()
  const [deleteTarget, setDeleteTarget] = React.useState(null)

  return (
    <>
      <TableContainer borderRadius="lg" boxShadow="lg" bg="white">
        <Table size="xs">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Slug</Th>
              <Th>Description</Th>
              <Th>Schemas</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {Object.values(datasets).map((dataset) => (
              <Tr
                key={dataset.id}
                cursor="pointer"
                onClick={() => navigate(`/datasets/${dataset.id}`)}
                _hover={{ bg: "gray.50" }}
              >
                <Td fontWeight="medium">
                  <AppLink to={`/datasets/${dataset.id}`}>
                    {dataset.name}
                  </AppLink>
                </Td>
                <Td>{dataset.slug}</Td>
                <Td color="Gray.600" fontSize="xs">
                  <Text noOfLines={1}>{dataset.description}</Text>
                </Td>
                <Td>{datasetSchemas[dataset.id]?.length || 0}</Td>
                <Td isNumeric>
                  {canDelete && (
                    <IconButton
                      aria-label={`Delete dataset ${dataset.name}`}
                      icon={<DeleteIcon />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteTarget(dataset)
                      }}
                    />
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {canDelete && deleteTarget && (
        <DeleteConfirmModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          type="dataset"
          name={deleteTarget.name}
          id={deleteTarget.id}
        />
      )}
    </>
  )
}

function DatasetCards({ datasets }) {
  return (
    <Flex gap={4} flexWrap="wrap" justifyContent="flex-start">
      {Object.values(datasets).map((dataset) => (
        <Box key={dataset.id}>
          <DatasetCard dataset={dataset} />
        </Box>
      ))}
    </Flex>
  )
}

export default function DatasetsList() {
  const user = useRouteLoaderData("auth")
  const [datasets] = Datasets.useState()
  const [datasetSchemas] = DatasetSchemas.useState()
  const [currentOrg] = CurrentOrg.useState()
  const [view, setView] = React.useState("table")
  const [createOpen, setCreateOpen] = React.useState(false)

  if (!user) return null

  const canDelete = isOrgAdmin(user, currentOrg)
  const hasDatasets = Object.keys(datasets).length > 0
  const datasetCount = Object.keys(datasets).length
  const { maxDatasets } = getPlanLimits(currentOrg)
  const atDatasetLimit = maxDatasets !== null && datasetCount >= maxDatasets

  return (
    <Flex
      direction="column"
      gap={4}
      justifyContent="flex-start"
      overflowY="auto"
      px={8}
      pb={4}
      pt={0}
    >
      <HStack gap={1}>
        <IconButton
          aria-label="Table view"
          icon={<DragHandleIcon />}
          size="sm"
          variant={view === "table" ? "solid" : "ghost"}
          colorScheme={view === "table" ? "blue" : "gray"}
          onClick={() => setView("table")}
        />
        <IconButton
          aria-label="Card view"
          icon={<GridIcon />}
          size="sm"
          variant={view === "cards" ? "solid" : "ghost"}
          colorScheme={view === "cards" ? "blue" : "gray"}
          onClick={() => setView("cards")}
        />
      </HStack>

      <CreateDatasetModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      {!hasDatasets && (
        <Box>
          <Heading variant="standardSub">Welcome to Blind Insight!</Heading>
          <Text>
            Start exploring the UI or check out the links below for the full
            developer experience.
          </Text>
          <Heading variant="standardSub" mt={6}>
            Developer Quickstart
          </Heading>
          <OrderedList>
            <ListItem>
              <Link href="https://docs.blindinsight.io/download/" target="new">
                Download the Blind Proxy
              </Link>
            </ListItem>
            <ListItem>
              <Link
                href="https://github.com/blind-insight/demo-datasets/releases"
                target="new"
              >
                Download a sample dataset, or use your own
              </Link>
            </ListItem>
            <ListItem>
              <Link
                href="https://docs.blindinsight.io/getting-started/"
                target="new"
              >
                Check out the Getting Started Guide
              </Link>
            </ListItem>
          </OrderedList>
        </Box>
      )}

      {view === "table" ? (
        <DatasetTable
          datasets={datasets}
          datasetSchemas={datasetSchemas}
          canDelete={canDelete}
        />
      ) : (
        <DatasetCards datasets={datasets} />
      )}
      <Button
        variant="primary"
        size="sm"
        rightIcon={<AddIcon />}
        maxWidth="fit-content"
        alignSelf="flex-end"
        onClick={() => setCreateOpen(true)}
        isDisabled={atDatasetLimit}
        title={
          atDatasetLimit
            ? `Your plan supports up to ${maxDatasets} dataset${
                maxDatasets === 1 ? "" : "s"
              }`
            : undefined
        }
      >
        Create Dataset
        {atDatasetLimit && ` (${datasetCount}/${maxDatasets})`}
      </Button>
    </Flex>
  )
}
